import { ChartWindowState, TimeRange } from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import { isTruthy } from '@ardoq/common-helpers';
import {
  CollapsedBooleanProperties,
  GridPrecisionOptionsData,
  SetWindowFunction,
  TimelineViewCommands,
  TimelineViewFieldsRegistry,
  TimelineViewSettings,
  WindowScalingLimitsForGrid,
} from '../../types';
import {
  ButtonConfig,
  SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { getTimeFrameSelectionDropdownOptions } from '../chartWindow/timeFrameSelection';
import { getWindowCenterForDate, isDateWithinRange } from '../../calculations';
import { currentDate, startOfToday } from '@ardoq/date-time';
import { DropdownItem, Notifier } from '@ardoq/dropdown-menu';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { clamp } from 'lodash';

const adjustZoom = (
  increment: boolean,
  windowCenter: Point,
  windowScale: number,
  setWindow: SetWindowFunction,
  scaleLimits: WindowScalingLimitsForGrid
) => {
  const step = windowScale * 0.2;

  const newWindowScale = clamp(
    windowScale + (increment ? -step : step),
    scaleLimits.min,
    scaleLimits.max
  );

  setWindow({
    windowCenter: newWindowScale === 1 ? [0.5, 0.5] : windowCenter,
    windowScale: newWindowScale,
  });
};

type GetZoomControlsArgs = ChartWindowState & {
  windowScalingLimitsForGrid: WindowScalingLimitsForGrid;
  viewInstanceId: string;
  commands: TimelineViewCommands;
  zoomInLabel?: string;
  zoomOutLabel?: string;
  resetZoomLabel?: string;
};

const timelineZoom =
  (increment: boolean) =>
  ({
    windowCenter,
    windowScale,
    windowScalingLimitsForGrid,
    viewInstanceId,
    commands,
  }: GetZoomControlsArgs) =>
  () =>
    adjustZoom(
      increment,
      windowCenter,
      windowScale,
      chartWindow => commands.setChartWindow(chartWindow, viewInstanceId),
      windowScalingLimitsForGrid
    );
export const [timelineZoomIn, timelineZoomOut] = [true, false].map(increment =>
  timelineZoom(increment)
);
export const timelineResetZoom =
  ({
    viewInstanceId,
    commands,
    windowScalingLimitsForGrid: { min: minLimit, max: maxLimit },
  }: GetZoomControlsArgs) =>
  () =>
    commands.setChartWindow(
      {
        windowCenter: [0.5, 0.5],
        windowScale: clamp(minLimit, maxLimit, 1),
      },
      viewInstanceId
    );
export const getZoomControls = (args: GetZoomControlsArgs) => {
  const {
    windowScale,
    windowScalingLimitsForGrid,
    zoomInLabel = 'Zoom in',
    zoomOutLabel = 'Zoom out',
    resetZoomLabel = 'Reset zoom',
  } = args;
  const { min: minLimit, max: maxLimit } = windowScalingLimitsForGrid;
  return [
    {
      id: 'zoomIn',
      label: zoomInLabel,
      type: SettingsType.BUTTON,
      onClick: timelineZoomIn(args),
      iconName: IconName.ZOOM_IN,
      isDisabled: windowScale <= minLimit,
    },
    {
      id: 'zoomOut',
      label: zoomOutLabel,
      type: SettingsType.BUTTON,
      onClick: timelineZoomOut(args),
      iconName: IconName.ZOOM_OUT,
      isDisabled: windowScale >= maxLimit,
    },
    {
      id: 'resetZoom',
      label: resetZoomLabel,
      type: SettingsType.BUTTON,
      onClick: timelineResetZoom(args),
      iconName: IconName.ZOOM_OUT_MAP,
    },
  ];
};

type GetTodayButtonArgs = {
  chartDomainRange: TimeRange;
  windowScale: number;
  currentWindowCenter: Point;
  viewInstanceId: string;
  commands: TimelineViewCommands;
  todayLabel?: string;
  todayNotInRangePopover?: string;
  noScrollPossiblePopover?: string;
};
export const getTodayButton = ({
  chartDomainRange,
  windowScale,
  currentWindowCenter,
  viewInstanceId,
  commands,
  todayLabel = 'Today',
  todayNotInRangePopover = 'Today is not within the chart range',
  noScrollPossiblePopover = 'Not available at this zoom level',
}: GetTodayButtonArgs) => {
  const isTodayWithinRange = isDateWithinRange(currentDate(), chartDomainRange);
  const isScrollPossible = windowScale < 1;
  return {
    id: 'today',
    label: todayLabel,
    type: SettingsType.BUTTON,
    onClick: () => {
      const todayWindowCenter = getWindowCenterForDate(
        startOfToday(),
        chartDomainRange,
        windowScale
      );

      if (
        todayWindowCenter &&
        todayWindowCenter[0] !== currentWindowCenter[0]
      ) {
        commands.setChartWindow(
          { windowCenter: todayWindowCenter },
          viewInstanceId
        );
      }
    },
    iconName: IconName.TODAY,
    isDisabled: !isScrollPossible || !isTodayWithinRange,
    popoverText: !isTodayWithinRange
      ? todayNotInRangePopover
      : !isScrollPossible
        ? noScrollPossiblePopover
        : undefined,
  };
};

type GetLeftSettingsBase = {
  viewSettings: TimelineViewSettings;
  timelineViewFieldsRegistry: TimelineViewFieldsRegistry;
  toggleCollapseMenuItems: ButtonConfig[];
  phaseFieldOptions: DropdownItem[];
  milestoneFieldOptions: DropdownItem[];
};

type GetWorkspaceLeftSettingsMenuConfigArgs = GetLeftSettingsBase & {
  onViewSettingsUpdate: OnViewSettingsUpdate;
  gridPrecisionOptionsData: GridPrecisionOptionsData;
  hasNewGrouping: boolean;
  isPresentationMode: boolean;
  openDateRangeViewSetting: Notifier;
  openMilestonesViewSetting: Notifier;
  selectTimelineLayoutLabel?: string;
  useNewGroupingLabel?: string;
  selectPhasesLabel?: string;
  showMilestonesLabel?: string;
  dateRangeLabel?: string;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  timelineLayoutSelectLabel?: string;
};

export const getLeftSettingsMenuConfigForWorkspaceMode = ({
  viewSettings,
  onViewSettingsUpdate,
  toggleCollapseMenuItems,
  gridPrecisionOptionsData,
  phaseFieldOptions,
  milestoneFieldOptions,
  hasNewGrouping,
  isPresentationMode,
  openDateRangeViewSetting,
  openMilestonesViewSetting,
  selectTimelineLayoutLabel = 'Select Timeline layout',
  useNewGroupingLabel = 'Use new grouping',
  selectPhasesLabel = 'Select phases',
  showMilestonesLabel = 'Show milestones',
  dateRangeLabel,
  startDatePlaceholder,
  endDatePlaceholder,
  timelineLayoutSelectLabel,
}: GetWorkspaceLeftSettingsMenuConfigArgs): SettingsConfig[] => {
  if (isPresentationMode) {
    return [...toggleCollapseMenuItems];
  }

  const timeFrameSelectionControls = {
    id: 'time-frame-selection',
    label: selectTimelineLayoutLabel,
    iconName: IconName.CALENDAR,
    type: SettingsType.DROPDOWN,
    options: getTimeFrameSelectionDropdownOptions({
      gridPrecisionOptionsData,
      viewSettings,
      onViewSettingsUpdate,
      dateRangeLabel,
      startDatePlaceholder,
      endDatePlaceholder,
      timelineLayoutSelectLabel,
    }),
    isKeepOpen: true,
  };

  const useNewGrouping = hasNewGrouping
    ? {
        id: 'useNewGrouping',
        label: useNewGroupingLabel,
        iconName: IconName.RHOMBUS,
        type: SettingsType.TOGGLE,
        isActive: Boolean(viewSettings.useNewGrouping),
        onViewSettingsUpdate,
      }
    : undefined;

  const selectPhases = {
    id: 'phaseFields',
    label: selectPhasesLabel,
    type: SettingsType.DROPDOWN,
    iconName: IconName.FORMAT_LIST_BULLETED,
    options: phaseFieldOptions,
    isKeepOpen: true,
    requestOpen: openDateRangeViewSetting,
  };
  const showMilestones = {
    id: 'milestones',
    label: showMilestonesLabel,
    type: SettingsType.DROPDOWN,
    iconName: IconName.RHOMBUS,
    options: milestoneFieldOptions,
    isKeepOpen: true,
    requestOpen: openMilestonesViewSetting,
  };

  return [
    timeFrameSelectionControls,
    selectPhases,
    ...toggleCollapseMenuItems,
    showMilestones,
    useNewGrouping,
  ].filter(isTruthy);
};

type ToggleCollapseArgs = {
  collapsedBooleanProperties: CollapsedBooleanProperties;
  viewInstanceId: string;
  commands: TimelineViewCommands;
  collapseAllLabel?: string;
  expandAllLabel?: string;
};
export const getToggleCollapseMenuItems = ({
  collapsedBooleanProperties,
  viewInstanceId,
  commands,
  collapseAllLabel = 'Collapse all',
  expandAllLabel = 'Expand all',
}: ToggleCollapseArgs) => {
  const collapseAllGroups = (isCollapsed: boolean) =>
    commands.collapseExpandAllGroups(isCollapsed, viewInstanceId);

  const {
    areAllCollapsed,
    areAllExpanded,
    areAllRootItemsCollapsed,
    hasNoCollapsible,
  } = collapsedBooleanProperties;

  return [
    {
      id: 'CollapseAll',
      label: collapseAllLabel,
      iconName: IconName.COLLAPSE_ALL,
      isDisabled: hasNoCollapsible,
      isActive: areAllCollapsed || areAllRootItemsCollapsed,
      onClick: () => collapseAllGroups(true),
      type: SettingsType.BUTTON,
    },
    {
      id: 'ExpandAll',
      label: expandAllLabel,
      iconName: IconName.EXPAND_ALL,
      isDisabled: hasNoCollapsible,
      isActive: areAllExpanded,
      onClick: () => collapseAllGroups(false),
      type: SettingsType.BUTTON,
    },
  ];
};
